import React, { useEffect, useState } from 'react';
import { getMyPromotionStats, getMyPromotions } from '../services/api';
import { ImpressionStats, Promotion } from '../services/api';
import './CustomerStatistics.css';

const REFRESH_INTERVAL_MS = 15000; // Live: refresh every 15 seconds

const CustomerStatistics: React.FC = () => {
  const [stats, setStats] = useState<ImpressionStats[]>([]);
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [statsMessage, setStatsMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const loadData = async () => {
    try {
      const [statsRes, promotionsRes] = await Promise.all([
        getMyPromotionStats(),
        getMyPromotions(),
      ]);
      setStats(statsRes.data || []);
      setPromotions(promotionsRes.data || []);
      setStatsMessage(statsRes.message ?? null);
    } catch (error) {
      console.error('Error loading statistics:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  const getPromotionTitle = (promotionId: number) => {
    const promo = promotions.find((p) => p.id === promotionId);
    return promo?.title || `Promotion #${promotionId}`;
  };

  const calculateCTR = (s: ImpressionStats): number => {
    if (s.total_impressions === 0) return 0;
    return parseFloat(((s.clicks / s.total_impressions) * 100).toFixed(2));
  };

  if (loading) {
    return <div className="customer-stats-loading">Loading statistics...</div>;
  }

  return (
    <div className="customer-statistics">
      <div className="customer-stats-header">
        <h2>Promotion Statistics (Live)</h2>
        <span className="live-badge">Updates every 15s</span>
      </div>

      {stats.length === 0 ? (
        <div className="empty-state">
          {statsMessage || 'No statistics for your promotions yet. Create a promotion and get some views!'}
        </div>
      ) : (
        <>
          <div className="stats-table-container">
            <table className="stats-table">
              <thead>
                <tr>
                  <th>Promotion</th>
                  <th>Total Impressions</th>
                  <th>Unique Users</th>
                  <th>Societies</th>
                  <th>Clicks</th>
                  <th>CTR</th>
                  <th>Story Views</th>
                  <th>Story Completes</th>
                </tr>
              </thead>
              <tbody>
                {stats.map((stat) => (
                  <tr key={stat.promotion_id}>
                    <td className="promotion-title">{getPromotionTitle(stat.promotion_id)}</td>
                    <td>{stat.total_impressions.toLocaleString()}</td>
                    <td>{stat.unique_users.toLocaleString()}</td>
                    <td>{stat.unique_societies.toLocaleString()}</td>
                    <td>{stat.clicks.toLocaleString()}</td>
                    <td>
                      <span className={`ctr-badge ${calculateCTR(stat) > 2 ? 'high' : calculateCTR(stat) > 1 ? 'medium' : 'low'}`}>
                        {calculateCTR(stat).toFixed(2)}%
                      </span>
                    </td>
                    <td>{stat.story_views.toLocaleString()}</td>
                    <td>{stat.story_completes.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="summary-cards">
            <div className="summary-card">
              <h3>Total Impressions</h3>
              <p className="summary-value">{stats.reduce((sum, s) => sum + s.total_impressions, 0).toLocaleString()}</p>
            </div>
            <div className="summary-card">
              <h3>Total Clicks</h3>
              <p className="summary-value">{stats.reduce((sum, s) => sum + s.clicks, 0).toLocaleString()}</p>
            </div>
            <div className="summary-card">
              <h3>Average CTR</h3>
              <p className="summary-value">
                {stats.length > 0 ? (stats.reduce((sum, s) => sum + calculateCTR(s), 0) / stats.length).toFixed(2) : 0}%
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CustomerStatistics;
